import { Flex } from "@chakra-ui/react";
import { useEffect, useState, memo } from "react";
import { ReaderOverlay } from "./components/overlay/ReaderOverlay";
import { ReaderReleasePages } from "./components/ReaderReleasePages";
import { useIsOverlayOpen } from "./hooks/hookstate/useIsOverlayOpen";
import { useSlideInMenu } from "../../components/slideInMenu/hooks/useSlideInMenu";
import { ActionOverlay } from "./components/horizontalReading/ActionOverlay";
import { ImmutableObject, useHookstate } from "@hookstate/core";
import { Page, Release } from "../../types";
import { CHAPTER_TYPES, READING_DIRECTION_TYPES } from "./constants";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import { readerDataStore } from "../../hookstate/readerDataStore";
import { BuyPreviewFooterBanner } from "./components/banners/BuyPreviewFooterBanner";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import { useReadingDirection } from "./hooks/useReadingDirection";
import { useReleasePagesData } from "./hooks/hookstate/useReleasePagesData";
import { useGetReadablePages } from "./hooks/useGetReadablePages";
import { useParams } from "react-router-dom";

const ReaderScreen = memo(function ReaderScreen() {
  const { openMenu, onCloseMenu } = useSlideInMenu();
  const thinScroll = useMobileBreakpoint();

  const releaseData = useHookstate(readerDataStore.releaseData).get({
    noproxy: true,
  });

  const documentTitle = constructDocumentTitle(releaseData);

  useTitleOnNavigation(documentTitle);

  return (
    <Flex
      alignItems="center"
      layerStyle="readerContainer"
      transition="left .2s linear"
      zIndex="auto"
      width="100%"
      minHeight="100dvh"
      height="auto"
      minWidth="100%"
      overflow="auto"
      style={{ scrollbarWidth: thinScroll ? "thin" : "auto" }}
    >
      <ActionOverlay>
        <ReaderReleasePages />
      </ActionOverlay>
      <ReaderOverlayAndMenu
        release={releaseData}
        onCloseMenu={onCloseMenu}
        openMenu={openMenu}
      />
    </Flex>
  );
});

export default ReaderScreen;

function ReaderOverlayAndMenu({
  openMenu,
  onCloseMenu,
  release,
}: {
  openMenu?: string;
  onCloseMenu: () => void;
  release: ImmutableObject<Release> | undefined;
}) {
  const { isOverlayOpen, toggleIsOverlayOpen } = useIsOverlayOpen();
  const [previewFooterDismissed, setPreviewFooterDismissed] = useState(false);
  const openModal = useHookstate(rootComponentsStore.funnelModal.type).get({
    noproxy: true,
  });

  const { pageOrder } = useParams();
  const { releasePagesData } = useReleasePagesData();
  const { readingDirection, comicIsVertical } = useReadingDirection();
  const { readablePages } = useGetReadablePages(releasePagesData as Page[]);

  /// Show the footer banner when the release changes,
  // like if they switch to a different release
  useEffect(() => {
    setPreviewFooterDismissed(false);
  }, [release?.id]);

  // Close menu when overlay closes (but not when just changing reading modes)
  useEffect(() => {
    if (isOverlayOpen) return;
    onCloseMenu();
  }, [isOverlayOpen, onCloseMenu]);

  const activePage = pageOrder ? parseInt(pageOrder) - 1 : 1;
  const readingInVertical =
    comicIsVertical || readingDirection === READING_DIRECTION_TYPES.vertical;
  const dismissInVertical =
    readingInVertical && readablePages.length - 1 === activePage;

  const hideBuyPreviewFooterBanner = !!(
    isOverlayOpen ||
    openModal ||
    dismissInVertical ||
    previewFooterDismissed
  );
  return (
    <>
      <ReaderOverlay
        isOpen={!!isOverlayOpen}
        onToggle={toggleIsOverlayOpen}
        openMenu={openMenu}
        onCloseMenu={onCloseMenu}
      />
      <BuyPreviewFooterBanner
        isDismissed={hideBuyPreviewFooterBanner}
        onDismiss={() => setPreviewFooterDismissed(true)}
      />
    </>
  );
}

function constructDocumentTitle(
  release?: ImmutableObject<Release>,
): string | null {
  if (!release) return null;
  return `Read ${release.localized_comic_name} ${CHAPTER_TYPES[release.chapter_type]} ${release.chapter} in ${release.lang_display} Online | GlobalComix`;
}
